/**
 * Stale-token refresh — the Firebase side of the interceptor's bounded wait for a usable login token.
 *
 * ── Why (2026-11-03) ─────────────────────────────────────────────────────────────────────────────
 * The token interceptor reads a cached token and sends the request tokenless when that token is expired, inside the 60 s skew,
 * or blank (src/utils/authHeaderPolicy.js: no_token / token_expiring). A SIGNED-IN tab gets there when Firebase's background
 * refresh did not run in time — a laptop that slept, a hidden tab whose timers were throttled. Prod, 2026-11-03: live customers'
 * whole bursts of calls went out tokenless and `get-credit-balance` (which enforces the login check) answered 401 → Lumigo alert.
 * src/utils/authSessionGate.js now waits, bounded, for `refresh()` and sends the request WITH the fresh token.
 *
 * ── The rule ─────────────────────────────────────────────────────────────────────────────────────
 *   • Only ever called for a signed-in user whose cached token is unusable and whose request the policy would tag.
 *   • FORCE a refresh (getIdTokenResult(true)): without it Firebase hands back the cached token while it has > 30 s left, and our
 *     policy refuses a token with < 60 s left — so a plain getIdToken() would leave the 30-60 s band tokenless.
 *   • ONE network refresh however many requests are waiting (a page load fires a dozen calls at once).
 *   • A failure backs off: for REFRESH_COOLDOWN_MS refresh() resolves null at once, so a Firebase that is down delays nothing twice.
 *   • Never rejects, never throws: every failure resolves null and the caller sends the request exactly as it does today.
 *   • The result of a user who is no longer the current user is dropped.
 *
 * Pure on purpose, ZERO imports: the Firebase user is injected, so cypress/node-tests/auth-token-refresh.test.js executes this
 * exact file. The wiring (the real auth.currentUser) lives in src/plugins/authTokenInterceptor.js, which must NOT call getIdToken
 * itself — cypress/node-tests/auth-token-wiring.test.js pins that.
 */

// After a failed refresh, how long refresh() answers null without asking Firebase again.
export const REFRESH_COOLDOWN_MS = 30 * 1000;

/**
 *   getUser   — () => the current Firebase user, or null (read fresh each time)
 *   onFresh   — ({ token, expiresAt }) => void, told about every successful refresh (the wiring updates its cached token)
 *   now       — injectable clock
 *   cooldownMs — injectable for tests
 * refresh() resolves { token, expiresAt (epoch ms) } or null.
 */
export function createTokenRefresher({ getUser, onFresh, now = Date.now, cooldownMs = REFRESH_COOLDOWN_MS } = {}) {
  let inFlight = null;
  let coolUntil = 0;

  function run() {
    let user;
    try { user = getUser && getUser(); } catch (_e) { return Promise.resolve(null); }
    if (!user || typeof user.getIdTokenResult !== 'function') return Promise.resolve(null);

    let pending;
    try { pending = Promise.resolve(user.getIdTokenResult(true)); } catch (_e) { pending = Promise.reject(_e); }

    return pending.then((result) => {
      let current = null;
      try { current = getUser && getUser(); } catch (_e) { current = null; }
      if (current !== user) return null; // signed out / switched account while we waited
      const token = result && result.token;
      const exp = result && result.claims && Number(result.claims.exp);
      if (typeof token !== 'string' || !token || token === 'undefined' || token === 'null' || !exp || !isFinite(exp)) {
        coolUntil = now() + cooldownMs;
        return null;
      }
      const fresh = { token, expiresAt: exp * 1000 };
      try { if (onFresh) onFresh(fresh); } catch (_e) { /* the caller still gets the token */ }
      return fresh;
    }).catch(() => {
      coolUntil = now() + cooldownMs;
      return null;
    });
  }

  return {
    refresh() {
      try {
        if (inFlight) return inFlight;
        if (now() < coolUntil) return Promise.resolve(null);
        inFlight = run().then((r) => { inFlight = null; return r; }, () => { inFlight = null; return null; });
        return inFlight;
      } catch (_e) {
        inFlight = null;
        return Promise.resolve(null);
      }
    },
  };
}
